import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import Keycloak from 'keycloak-js';
import { firstValueFrom, timeout } from 'rxjs';
import { decodeJwtPayload, USER_MANAGEMENT_API } from '@luzula/luzula-lib';

/** Timeout für Admin-API Requests in Millisekunden */
const API_TIMEOUT = 15000;

/** Minimale Token-Gültigkeit in Sekunden - Token wird refreshed wenn weniger übrig */
const MIN_TOKEN_VALIDITY = 30;

export type KeycloakAdminAuthErrorKind = 'session' | 'forbidden';

/**
 * Typisierter Fehler für Auth-/Berechtigungsprobleme der Admin-API.
 *
 * Bewusst KEIN `keycloak.login()`-Redirect mehr im Service: ein Full-Page-
 * Redirect zerstört offene Modals (z.B. "Projekt bearbeiten") und führt bei
 * fehlender Admin-Berechtigung in eine Endlosschleife. Stattdessen wird dieser
 * Fehler geworfen und der Aufrufer entscheidet (Meldung zeigen, Modal offen
 * lassen). `kind`:
 *  - `session`   → Token abgelaufen/nicht erneuerbar (Neu-Anmeldung nötig)
 *  - `forbidden` → angemeldet, aber keine Admin-/`view-users`-Berechtigung
 */
export class KeycloakAdminAuthError extends Error {
	constructor(public readonly kind: KeycloakAdminAuthErrorKind, message: string) {
		super(message);
		this.name = 'KeycloakAdminAuthError';
	}
}

export interface KeycloakUser {
	id: string;
	username: string;
	email?: string;
	firstName?: string;
	lastName?: string;
	enabled: boolean;
	emailVerified?: boolean;
	createdTimestamp?: number;
}

export interface KeycloakRole {
	id: string;
	name: string;
	description?: string;
	composite?: boolean;
	children?: KeycloakRole[];
}

export interface KeycloakClient {
	id: string;
	clientId: string;
	name?: string;
}

export interface KeycloakGroup {
	id: string;
	name: string;
	path?: string;
}

@Injectable({
	providedIn: 'root'
})
export class KeycloakAdminService {
	private readonly keycloak = inject(Keycloak);
	private readonly http = inject(HttpClient);
	/**
	 * Benutzerverwaltung der luzula-api. Liefert über
	 * `/admin/users/role-members` die Mitglieder einer Rolle, ohne dass das
	 * eigene Konto Keycloak lesen darf — siehe {@link getUsersWithClientRole}.
	 */
	private readonly userMgmtApi = inject(USER_MANAGEMENT_API, { optional: true });

	/** Cache clientId→Client (UUID ändert sich praktisch nie) — spart 1 Lookup pro Benutzer. */
	private readonly clientCache = new Map<string, KeycloakClient | undefined>();
	/** Cache `${groupId}|${clientUuid}` → effektive Client-Rollen der Gruppe (von vielen Usern geteilt). */
	private readonly groupClientRoleCache = new Map<string, KeycloakRole[]>();

	/** Leert die Lookup-Caches (Client-UUID + Gruppen-Rollen). Für erzwungene Frische. */
	clearRoleCaches(): void {
		this.clientCache.clear();
		this.groupClientRoleCache.clear();
	}

	private get keycloakUrl(): string {
		return this.keycloak.authServerUrl ?? '';
	}

	private get realm(): string {
		return this.keycloak.realm ?? '';
	}

	/**
	 * Stellt sicher, dass das Token gültig ist und gibt die Headers zurück.
	 * Nutzt manuellen Token-Refresh als Fallback wenn keycloak-js updateToken
	 * versagt. Löst KEINEN Login-Redirect aus — bei Scheitern wird ein
	 * `KeycloakAdminAuthError('session')` geworfen (Aufrufer entscheidet).
	 *
	 * @param forceRefresh erzwingt einen Token-Refresh unabhängig von der
	 *   Restgültigkeit (für genau-einmal-Retry nach einem 401).
	 */
	private async getHeaders(forceRefresh = false): Promise<HttpHeaders> {
		// isTokenExpired wirft einen String ('Not authenticated.') wenn tokenParsed undefined ist
		// (z.B. nach Auto-Logout durch keycloak-angular sessionTimeout)
		let needsRefresh = forceRefresh;
		if (!needsRefresh) {
			try {
				needsRefresh = !!this.keycloak.isTokenExpired?.(MIN_TOKEN_VALIDITY);
			} catch {
				needsRefresh = true;
			}
		}

		if (needsRefresh || !this.keycloak.token) {
			// Zuerst keycloak-js updateToken versuchen (-1 = erzwingen)
			let refreshed = false;
			try {
				await this.keycloak.updateToken(forceRefresh ? -1 : MIN_TOKEN_VALIDITY);
				refreshed = !!this.keycloak.token && !this.keycloak.isTokenExpired?.(MIN_TOKEN_VALIDITY);
			} catch { /* Fallback unten */ }

			// Fallback: Manueller Refresh über Token-Endpoint
			if (!refreshed) {
				const manualOk = await this.refreshTokenManually();
				if (!manualOk) {
					throw new KeycloakAdminAuthError('session', 'Anmeldung abgelaufen - bitte neu anmelden');
				}
			}
		}

		const token = this.keycloak.token;
		if (!token) {
			throw new KeycloakAdminAuthError('session', 'Kein gültiges Anmeldetoken vorhanden');
		}

		return new HttpHeaders({
			'Authorization': `Bearer ${token}`,
			'Content-Type': 'application/json'
		});
	}

	private isHttpStatus(err: unknown, status: number): boolean {
		return err instanceof HttpErrorResponse && err.status === status;
	}

	/**
	 * Zentraler GET gegen die Admin-API mit robuster Auth-Behandlung:
	 *  - 401 → genau EINMAL mit erzwungenem Token-Refresh wiederholen
	 *  - danach weiterhin 401 → `KeycloakAdminAuthError('session')`
	 *  - 403 → `KeycloakAdminAuthError('forbidden')` (kein Retry, kein Redirect)
	 * Es wird nie automatisch `keycloak.login()` aufgerufen.
	 */
	private async authedGet<T>(url: string): Promise<T> {
		const doGet = async (force: boolean): Promise<T> => {
			const headers = await this.getHeaders(force);
			return firstValueFrom(this.http.get<T>(url, { headers }).pipe(timeout(API_TIMEOUT)));
		};
		try {
			return await doGet(false);
		} catch (err) {
			if (this.isHttpStatus(err, 403)) {
				throw new KeycloakAdminAuthError('forbidden', 'Keine Berechtigung für die Benutzerverwaltung');
			}
			if (!this.isHttpStatus(err, 401)) throw err;
			// Token evtl. serverseitig invalidiert obwohl lokal "gültig":
			// einmal erzwungen refreshen und genau einmal wiederholen.
			try {
				return await doGet(true);
			} catch (retryErr) {
				if (this.isHttpStatus(retryErr, 403)) {
					throw new KeycloakAdminAuthError('forbidden', 'Keine Berechtigung für die Benutzerverwaltung');
				}
				if (this.isHttpStatus(retryErr, 401)) {
					throw new KeycloakAdminAuthError('session', 'Anmeldung für die Benutzerverwaltung abgelaufen');
				}
				throw retryErr;
			}
		}
	}

	/**
	 * Manueller Token-Refresh über den Keycloak-Token-Endpoint.
	 * Funktioniert unabhängig von keycloak-js internem State (z.B. nach loginDirect).
	 */
	private async refreshTokenManually(): Promise<boolean> {
		const refreshToken = this.keycloak.refreshToken;
		if (!refreshToken) return false;

		const tokenUrl = `${this.keycloakUrl}/realms/${this.realm}/protocol/openid-connect/token`;
		try {
			const response = await fetch(tokenUrl, {
				method: 'POST',
				headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
				body: new URLSearchParams({
					grant_type: 'refresh_token',
					client_id: this.keycloak.clientId ?? 'bpweb',
					refresh_token: refreshToken
				})
			});

			if (!response.ok) return false;

			const data = await response.json();

			// Tokens auf Keycloak-Instanz setzen
			const kc = this.keycloak as any;
			kc.token = data.access_token;
			kc.refreshToken = data.refresh_token;
			if (data.id_token) kc.idToken = data.id_token;

			// Token parsen (UTF-8-/Base64URL-sicher)
			kc.tokenParsed = decodeJwtPayload(data.access_token as string);

			return true;
		} catch {
			return false;
		}
	}

	async getUsers(max = 100, first = 0): Promise<KeycloakUser[]> {
		const url = `${this.keycloakUrl}/admin/realms/${this.realm}/users?max=${max}&first=${first}`;
		return this.authedGet<KeycloakUser[]>(url);
	}

	/** Lädt alle Seiten einer paginierten Admin-Liste (first/max), bis eine Teilseite kommt. */
	private async getAllPaged<T>(buildUrl: (first: number, max: number) => string, pageSize = 100): Promise<T[]> {
		const out: T[] = [];
		let first = 0;
		for (;;) {
			const page = await this.authedGet<T[]>(buildUrl(first, pageSize));
			if (!page?.length) break;
			out.push(...page);
			if (page.length < pageSize) break;
			first += pageSize;
		}
		return out;
	}

	/** Nutzer mit DIREKT zugewiesener Client-Rolle (paginiert). */
	async getClientRoleUsers(clientUuid: string, roleName: string): Promise<KeycloakUser[]> {
		const enc = encodeURIComponent(roleName);
		return this.getAllPaged<KeycloakUser>((first, max) =>
			`${this.keycloakUrl}/admin/realms/${this.realm}/clients/${clientUuid}/roles/${enc}/users?first=${first}&max=${max}`);
	}

	/** Gruppen, denen die Client-Rolle zugewiesen ist (paginiert). */
	async getClientRoleGroups(clientUuid: string, roleName: string): Promise<KeycloakGroup[]> {
		const enc = encodeURIComponent(roleName);
		return this.getAllPaged<KeycloakGroup>((first, max) =>
			`${this.keycloakUrl}/admin/realms/${this.realm}/clients/${clientUuid}/roles/${enc}/groups?first=${first}&max=${max}`);
	}

	/** Direkte Mitglieder einer Gruppe (paginiert). */
	async getGroupMembers(groupId: string): Promise<KeycloakUser[]> {
		return this.getAllPaged<KeycloakUser>((first, max) =>
			`${this.keycloakUrl}/admin/realms/${this.realm}/groups/${groupId}/members?first=${first}&max=${max}`);
	}

	/**
	 * Mitglieder einer Rolle aus dem Rollen-Endpunkt der luzula-api
	 * (`/admin/users/role-members`, seit 2026-08-29). Der Server fragt Keycloak
	 * mit seinem Dienstkonto — das eigene Konto braucht dafür keine
	 * Leserechte im Realm. Direkt zugewiesene und über Gruppen geerbte Rollen
	 * sind beide enthalten.
	 *
	 * `null` heißt „Weg steht nicht offen" (ältere API, kein Zugriff, Server
	 * weg); dann bleibt der Keycloak-Direktweg.
	 */
	private async rollenMitgliederVomServer(clientId: string, roleName: string, groupScope?: string[]): Promise<KeycloakUser[] | null> {
		if (typeof this.userMgmtApi?.listRoleMembers !== 'function') return null;
		try {
			const res = await firstValueFrom(
				this.userMgmtApi.listRoleMembers(clientId, roleName, groupScope).pipe(timeout(API_TIMEOUT))
			);
			// Der Endpunkt liefert nur eingeschaltete Konten und keine E-Mails.
			return (res?.users ?? []).map(u => ({
				id: u.id,
				username: u.username,
				firstName: u.firstName || undefined,
				lastName: u.lastName || undefined,
				enabled: true,
			}));
		} catch (err) {
			console.warn(`[KeycloakAdmin] Rollen-Mitglieder für "${roleName}" nicht abrufbar, weiche auf Keycloak aus:`, err);
			return null;
		}
	}

	/**
	 * Alle Nutzer, die eine Client-Rolle besitzen — DIREKT zugewiesen ODER als
	 * Mitglied einer Gruppe, die die Rolle trägt.
	 *
	 * Zuerst der Rollen-Endpunkt der luzula-api (offen für jeden, der in dieser
	 * App eine Rolle hat), sonst Keycloak direkt mit dem Token des angemeldeten
	 * Nutzers. Der Direktweg ist rollen-zentrisch statt "alle laden + filtern":
	 * unabhängig von der Gesamt-Nutzerzahl, kein 100er-Limit, keine
	 * O(N)-Rollenabfrage. Gruppen-Fehler degradieren auf die direkten Nutzer
	 * (kein harter Abbruch).
	 *
	 * Hinweis: Mitglieder von UNTERgruppen einer rollen-tragenden Gruppe werden
	 * (noch) nicht expandiert — passt zur aktuellen, flachen Gruppenstruktur.
	 */
	async getUsersWithClientRole(clientId: string, roleName: string, groupScope?: string[]): Promise<KeycloakUser[]> {
		const vomServer = await this.rollenMitgliederVomServer(clientId, roleName, groupScope);
		// D2 (feature/user-groups): Mit gesetztem groupScope ist die serverseitig auf den
		// Gruppen-Teilbaum gefilterte Liste massgeblich — AUCH wenn sie leer ist (alle
		// Kandidaten gruppenfremd). Ohne diese Sonderbehandlung wuerde der Keycloak-
		// Direkt-Fallback unten die Einschraenkung umgehen. Nur bei Server-Fehler (null)
		// faellt es auf den (ungefilterten) Direktweg zurueck.
		if (vomServer !== null && (groupScope?.length || vomServer.length > 0)) return vomServer;

		const client = await this.getClientByClientId(clientId);
		if (!client) return vomServer ?? [];

		const byId = new Map<string, KeycloakUser>();
		for (const u of await this.getClientRoleUsers(client.id, roleName)) {
			if (u?.id) byId.set(u.id, u);
		}

		let groups: KeycloakGroup[] = [];
		try {
			groups = await this.getClientRoleGroups(client.id, roleName);
		} catch (err) {
			console.warn(`Failed to load groups for role ${roleName}:`, err);
		}
		for (const g of groups) {
			try {
				for (const u of await this.getGroupMembers(g.id)) {
					if (u?.id) byId.set(u.id, u);
				}
			} catch (err) {
				console.warn(`Failed to load members of group ${g.name}:`, err);
			}
		}

		return [...byId.values()];
	}

	async searchUsers(search: string, max = 20): Promise<KeycloakUser[]> {
		const url = `${this.keycloakUrl}/admin/realms/${this.realm}/users?search=${encodeURIComponent(search)}&max=${max}`;
		return this.authedGet<KeycloakUser[]>(url);
	}

	async getUserRoles(userId: string): Promise<KeycloakRole[]> {
		const url = `${this.keycloakUrl}/admin/realms/${this.realm}/users/${userId}/role-mappings/realm`;
		return this.authedGet<KeycloakRole[]>(url);
	}

	async getUserEffectiveRoles(userId: string): Promise<KeycloakRole[]> {
		const url = `${this.keycloakUrl}/admin/realms/${this.realm}/users/${userId}/role-mappings/realm/composite`;
		return this.authedGet<KeycloakRole[]>(url);
	}

	async getRealmRoles(): Promise<KeycloakRole[]> {
		const url = `${this.keycloakUrl}/admin/realms/${this.realm}/roles`;
		return this.authedGet<KeycloakRole[]>(url);
	}

	async getCompositeRoleChildren(roleName: string): Promise<KeycloakRole[]> {
		const url = `${this.keycloakUrl}/admin/realms/${this.realm}/roles/${encodeURIComponent(roleName)}/composites`;
		return this.authedGet<KeycloakRole[]>(url);
	}

	async getUserRolesExpanded(userId: string): Promise<KeycloakRole[]> {
		const roles = await this.getUserRoles(userId);
		for (const role of roles) {
			if (role.composite) {
				role.children = await this.getCompositeRoleChildren(role.name);
			}
		}
		return roles;
	}

	async getClientByClientId(clientId: string): Promise<KeycloakClient | undefined> {
		if (this.clientCache.has(clientId)) return this.clientCache.get(clientId);
		const url = `${this.keycloakUrl}/admin/realms/${this.realm}/clients?clientId=${encodeURIComponent(clientId)}`;
		const clients = await this.authedGet<KeycloakClient[]>(url);
		const client = clients?.[0];
		this.clientCache.set(clientId, client);
		return client;
	}

	/** Gruppen, in denen der Benutzer direkt Mitglied ist. */
	async getUserGroups(userId: string): Promise<KeycloakGroup[]> {
		const url = `${this.keycloakUrl}/admin/realms/${this.realm}/users/${userId}/groups`;
		return this.authedGet<KeycloakGroup[]>(url);
	}

	/** Effektive Client-Rollen einer Gruppe (inkl. Composite-Auflösung), gecacht. */
	async getGroupClientEffectiveRoles(groupId: string, clientUuid: string): Promise<KeycloakRole[]> {
		const key = `${groupId}|${clientUuid}`;
		const cached = this.groupClientRoleCache.get(key);
		if (cached) return cached;
		const url = `${this.keycloakUrl}/admin/realms/${this.realm}/groups/${groupId}/role-mappings/clients/${clientUuid}/composite`;
		const roles = await this.authedGet<KeycloakRole[]>(url);
		this.groupClientRoleCache.set(key, roles);
		return roles;
	}

	/**
	 * Über Gruppenmitgliedschaft vererbte Client-Rollen des Benutzers.
	 * Keycloak liefert Gruppen-Rollen NICHT in den User-Role-Mappings — daher
	 * separat über die Gruppen auflösen. Degradiert bei Fehlern (z.B. fehlende
	 * Gruppen-Leseberechtigung) auf [] statt die gesamte Auswahl zu sprengen.
	 *
	 * Hinweis: Es werden die Rollen der Gruppen aufgelöst, in denen der Benutzer
	 * DIREKT Mitglied ist. Rollen, die ausschließlich auf einer ELTERN-Gruppe einer
	 * Untergruppe hängen, werden (noch) nicht berücksichtigt.
	 */
	private async getUserGroupClientRoles(userId: string, clientUuid: string): Promise<KeycloakRole[]> {
		let groups: KeycloakGroup[];
		try {
			groups = await this.getUserGroups(userId);
		} catch (err) {
			console.warn(`Failed to load groups for user ${userId}:`, err);
			return [];
		}
		const out: KeycloakRole[] = [];
		const seen = new Set<string>();
		for (const g of groups) {
			let roles: KeycloakRole[] = [];
			try {
				roles = await this.getGroupClientEffectiveRoles(g.id, clientUuid);
			} catch (err) {
				console.warn(`Failed to load client roles for group ${g.name}:`, err);
			}
			for (const r of roles) {
				if (!seen.has(r.name)) { seen.add(r.name); out.push(r); }
			}
		}
		return out;
	}

	async getUserClientRoles(userId: string, clientUuid: string): Promise<KeycloakRole[]> {
		const url = `${this.keycloakUrl}/admin/realms/${this.realm}/users/${userId}/role-mappings/clients/${clientUuid}`;
		return this.authedGet<KeycloakRole[]>(url);
	}

	async getUserClientEffectiveRoles(userId: string, clientUuid: string): Promise<KeycloakRole[]> {
		const url = `${this.keycloakUrl}/admin/realms/${this.realm}/users/${userId}/role-mappings/clients/${clientUuid}/composite`;
		return this.authedGet<KeycloakRole[]>(url);
	}

	async getClientCompositeRoleChildren(clientUuid: string, roleName: string): Promise<KeycloakRole[]> {
		const url = `${this.keycloakUrl}/admin/realms/${this.realm}/clients/${clientUuid}/roles/${encodeURIComponent(roleName)}/composites`;
		return this.authedGet<KeycloakRole[]>(url);
	}

	async getUserClientRolesExpanded(userId: string, clientId: string): Promise<KeycloakRole[]> {
		const client = await this.getClientByClientId(clientId);
		if (!client) return [];

		// Get direct roles (for showing which are directly assigned)
		const directRoles = await this.getUserClientRoles(userId, client.id);
		const directRoleNames = new Set(directRoles.map(r => r.name));

		// Get all effective roles (includes inherited)
		const effectiveRoles = await this.getUserClientEffectiveRoles(userId, client.id);

		// Build result: direct roles with children expanded, plus mark inherited
		for (const role of directRoles) {
			if (role.composite) {
				role.children = await this.getClientCompositeRoleChildren(client.id, role.name);
			}
		}

		// Add effective roles that are not direct (inherited from realm roles)
		const inheritedRoles = effectiveRoles.filter(r => !directRoleNames.has(r.name));

		// Über Gruppen vererbte Client-Rollen ergänzen (fehlen in den User-Role-Mappings).
		const groupRoles = await this.getUserGroupClientRoles(userId, client.id);
		const seen = new Set<string>([...directRoleNames, ...inheritedRoles.map(r => r.name)]);
		const groupOnlyRoles = groupRoles.filter(r => {
			if (seen.has(r.name)) return false;
			seen.add(r.name);
			return true;
		});

		return [...directRoles, ...inheritedRoles, ...groupOnlyRoles];
	}
}
